import React from 'react'
import styled, {css} from 'styled-components'
import {black100, black700, blue300, borderLight, cinnabar300} from '../../theme/colors'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import EyeIcon from '@material-ui/icons/RemoveRedEye';
import EditIcon from "@material-ui/icons/Edit";
import capitializeFirstChar from '../../helpers/utils/capitializeFirstChar';
import { isMobileScreen } from '../../helpers/isMobileScreen';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';

const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 8px;
  background-color: #f8fafc;
  gap: 12px;
`;

const MobileSelectAllBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: white;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 4px;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.05);

  input[type="checkbox"],
  span {
    cursor: pointer;
  }
`;

const MobileCard = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-left: 4px solid #4770C1;
  padding: 12px 14px;
  box-shadow: 0 2px 4px rgb(0 0 0 / 0.02);
  transition: all 0.25s ease-in-out;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 12px rgb(0 0 0 / 0.05);
    border-color: rgba(71, 112, 193, 0.3);
  }
`;

const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 8px;
`;

const MobileCardTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const MobileCardCheckbox = styled.div`
  display: flex;
  align-items: center;
`;

const MobileCardTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.4;
  word-break: break-word;
  letter-spacing: 0.025em;
`;

const MobileCardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: 12px;
  
  span {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: rgba(71, 112, 193, 0.08);
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    border: 1px solid rgba(71, 112, 193, 0.1);
    
    &:hover {
      background-color: rgba(71, 112, 193, 0.15);
      transform: scale(1.05);
    }
  }
`;

const MobileCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const MobileCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  padding: 6px 0;
  border-bottom: 1px solid #f8fafc;
  &:last-child {
    border-bottom: none;
  }
`;

const MobileCardLabel = styled.span`
  color: #64748b;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin-right: 12px;
  white-space: nowrap;
`;

const MobileCardValue = styled.span`
  color: #334155;
  font-size: 13px;
  font-weight: 600;
  text-align: right;
  word-break: break-word;
  max-width: 65%;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;

  > div {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  button {
    min-height: 26px !important;
    height: 26px !important;
    padding: 0 8px !important;
    font-size: 11.5px !important;
    border-radius: 5px !important;
    min-width: unset !important;
    width: auto !important;

    span {
      font-size: 11.5px !important;
    }

    svg {
      width: 13px !important;
      height: 13px !important;
    }
  }
`;

const ExpandToggleButton = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  gap: 4px;
  padding: 4px 12px;
  margin-top: 4px;
  margin-left: auto;
  margin-right: auto;
  background-color: #f1f5f9;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  color: #3b82f6;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
  width: fit-content;

  &:hover {
    color: #1d4ed8;
    background-color: #e2e8f0;
  }

  svg {
    font-size: 15px !important;
  }
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  word-break: break-word;
  max-width: 65%;
  background-color: ${props => props.bg};
  color: ${props => props.color};
  border: 1px solid ${props => props.border || 'transparent'};
  text-transform: uppercase;
  letter-spacing: 0.025em;

  div {
    display: inline;
    margin: 0;
    padding: 0;
    font-weight: 600;
  }
`;

export const StyledTable = styled.div`
  display: block;
  background: white;

  /* Ensure split panels use fixed pixel widths, while default tables maintain 100% width for internal scrolling */
  min-width: ${props => {
    if (props.position === 'left') {
      const colsWidth = props.columns ? props.columns.reduce((sum, col) => sum + (col.width ? parseInt(col.width, 10) : 175), 0) : (props.length ? parseInt(props.length, 10) * 175 : 175);
      return `${colsWidth + 24}px`;
    }
    if (props.position === 'right' || props.rightPanel) {
      const totalWidth = props.columns ? props.columns.reduce((sum, col) => sum + (col.width ? parseInt(col.width, 10) : 175), 0) : (props.length ? parseInt(props.length, 10) * 175 : 175);
      return `${totalWidth}px`;
    }
    return '100%';
  }};

  ${props =>
          props.position === 'left'
                  ? `
      max-width: ${(props.columns ? props.columns.reduce((sum, col) => sum + (col.width ? parseInt(col.width, 10) : 175), 0) : (props.length ? parseInt(props.length, 10) * 175 : 175)) + 24}px;
      margin-right: 1px;
  `
                  : ''}
  ${props =>
          props.position === 'right'
                  ? `
      max-width: ${props.columns ? props.columns.reduce((sum, col) => sum + (col.width ? parseInt(col.width, 10) : 175), 0) : (props.length ? parseInt(props.length, 10) * 175 : 175)}px;
      margin-left: 1px;
  `
                  : ''};
  width: ${props => props.width || '100%'};
  min-height: ${props => props.minHeight || 'auto'};
  padding: ${props => props.rightPanel ? '8px 0 8px 0' : '8px 0 8px 24px'};

  /* Restore original scrollable behaviour for default layouts */
  ${props => (!props.position && !props.rightPanel) ? `
    overflow: auto;
    max-height: 600px;
  ` : ''}
`

export const THEAD = styled.div`
  min-height: 45px;
  height: auto;
  display: inline-flex;
  min-width: 100%;
  align-items: center;
  z-index: ${props => props.singleScrollTable ? 3 : 1};
  border-bottom: ${props => props.isBorder && `1px solid ${borderLight}`};
  background: white;
  ${props =>
          props.hasFixedElements
                  ? ''
                  : `
      position: sticky;
      top: -8px;
      z-index: 3;
  `}

  ${props => props.fluidTable &&
          css`
            border-bottom: 1px solid ${borderLight};
          `
  }
`

export const TD = styled.div`
  padding: ${props => props.noPadding ? 0 : (props.padding || '0 12px')};
  height: ${props => (props.height ? props.height : '55px')};
  min-width: ${props => (props.width ? props.width : '175px')};
  max-width: ${props => (props.width ? props.width : '175px')};
  display: flex;
  flex-flow: ${props => (props.flexFlow || "row")};
  align-items: ${props => (props.alignItem ? props.alignItem : 'center')};
  justify-content: ${props => props.justify ? props.justify : "flex-start"};
  background: ${props => props.isSelected && `${black100} !important` };
  box-sizing: border-box;
  border-bottom: 1px solid #E6E8F1;
  
  &:hover {
    ${props =>
            props.hoverable
                    ? `background-color: ${black700};
    border: 0.5px solid ${black700};
    cursor: pointer`
                    : ''}
  }

  // white-space: ${props => props.whitespace || 'nowrap'};
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    // overflow: visible;
    // white-space: normal;
    ${props => props.wordBreak && css`
      word-break: ${props.wordBreak || 'break-all'};
    `
    }
     
  }

  ${props => props.hasBorder && css`
    border: 1px solid ${borderLight};
    border-bottom: none;
  `}
`

export const THD = styled(TD)`
  font-weight: bold;
  background: white;
  min-height: 45px;
  height: ${props => props.height || 'auto'};
  padding: ${props => props.noPadding ? 0 : (props.padding || '6px 12px')};
  justify-content: ${props => props.justify || "flex-start"};
  border-bottom: ${props => !props.isBorder && `1px solid ${borderLight}`};
  white-space: ${props => props.whitespace || 'normal'};
  line-height: 1.25;
  ${props => props.sortable &&
          css`
            cursor: pointer;
          `
  }
  ${props => props.noBorder && css`
    border: none;
  `}
`

export const TBODY = styled.div`
  text-align: left;
  /* Remove divider from last row's TD cells */
  & > div:last-child > div {
    border-bottom: none;
  }
`

const RowAction = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;

  position: absolute;
  right: 15px;
  opacity: 0;
  z-index: 3;

  width: ${props => props.rowActionElementWidth || '43px'};
  height: 41px;
  transition: all 0.2s ease-in-out;
  cursor: pointer;

  svg {
    width: 20px;
  }
`


export const TR = styled.div`
  display: inline-flex;
  min-width: 100%;
  align-items: center;
  height: ${props => props.height || '55px'};
  position: relative;
  background: ${props => {
    if (props.isSelected) return `${black100} !important`;
    if (props.isHovered) return '#F1F5F9 !important';
    if (props.useStatusColor && props.status === 'temp') return `${black100}`; 
    return 'white';
  }};

  /* Row divider — painted on top of all child TDs via ::after + z-index */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 1px;
    background-color: #E6E8F1;
    pointer-events: none;
    z-index: 1;
  }

  &:hover {
    background: ${props => props.isSelected ? `${black100} !important` : '#F1F5F9 !important'};
    cursor: ${props => props.isCursor ? 'pointer' : 'default'};

    ${RowAction} {
      display: flex;
      right: 0px;
      opacity: 1;
    }
  }

  ${props => props.disablePointer && css`
    cursor: auto !important;
  `}

  ${props => props.noBorder && css`
    border: none;
  `}
`;

const getWorkflowStatusColors = (value) => {
  if (!value) return { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' };
  
  // Extract string content if it's an element or object
  let text = '';
  if (typeof value === 'string') {
    text = value.toLowerCase();
  } else if (value && value.props && typeof value.props.children === 'string') {
    text = value.props.children.toLowerCase();
  } else {
    text = String(value).toLowerCase();
  }

  if (text.includes('pending') || text.includes('wip')) {
    return { bg: 'rgba(245, 158, 11, 0.08)', color: '#d97706', border: 'rgba(245, 158, 11, 0.2)' }; // amber/orange
  }
  if (text.includes('approve') || text.includes('success') || text.includes('accept') || text.includes('done') || text.includes('complete')) {
    return { bg: 'rgba(16, 185, 129, 0.08)', color: '#10b981', border: 'rgba(16, 185, 129, 0.2)' }; // green
  }
  if (text.includes('reject') || text.includes('decline') || text.includes('fail') || text.includes('cancel')) {
    return { bg: 'rgba(239, 68, 68, 0.08)', color: '#ef4444', border: 'rgba(239, 68, 68, 0.2)' }; // red
  }
  
  return { bg: 'rgba(59, 130, 246, 0.08)', color: '#3b82f6', border: 'rgba(59, 130, 246, 0.2)' }; // blue/default highlight
};

class Table extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      expandedCards: {}
    };
  }

  toggleCardExpand = (index) => {
    this.setState(prevState => ({
      expandedCards: {
        ...prevState.expandedCards,
        [index]: !prevState.expandedCards[index]
      }
    }));
  };

  render() {
    const {
      columns,
      data,
      rowStyle,
      position,
      onRowClick,
      updateOrder,
      order,
      hasFixedElements = false,
      enableCheckbox = false,
      fluidTable = false,
      rowActionType = 'VIEW',
      rowActionElement,
      rowHeight,
      disableRowClick = false,
      rowActionElementWidth,
      selectedOutletRows = [],
      isCursor = true,
      isPosition = true,
      isBorder = true,
      applyFilter,
      useStatusColor = false,
      rightPanel = false,
      hoveredRowIndex,
      onRowHover
    } = this.props;

    const isMobile = isMobileScreen();

    if (isMobile && columns.length > 0) {
      const chooseColumn = columns.find(c => c.key === 'choose' || (c.name && typeof c.name === 'object'));
      const nameColumn = columns.find(c => c.name && typeof c.name === 'string' && (c.name.toLowerCase().includes('name') || c.name.toLowerCase().includes('title') || c.name.toLowerCase().includes('rsid')));

      return (
        <MobileContainer>
          {chooseColumn && (
            <MobileSelectAllBar>
              {chooseColumn.name}
              <span>Select All (Page Items)</span>
            </MobileSelectAllBar>
          )}
          {data.map((item, inx) => {
            const headerText = nameColumn
              ? (nameColumn.render
                  ? nameColumn.render(nameColumn.key && nameColumn.key.split('.').reduce((o, i) => o && o[i], item), item, inx)
                  : nameColumn.key && nameColumn.key.split('.').reduce((o, i) => o && o[i], item))
              : `Item #${inx + 1}`;

            const isExpanded = this.state.expandedCards[inx] || false;

            // Get all displayable data columns (excluding checkboxes and main header RSID)
            const displayableColumns = columns.filter(column => {
              if (column === nameColumn || column === chooseColumn) return false;
              if (column.key === 'choose') return false;
              if (typeof column.name !== 'string' || !column.name) return false;
              
              const val = column.key && column.key.split('.').reduce((o, i) => o && o[i], item);
              if (val === undefined || val === null) return false;
              if (typeof val === 'string' && val.trim() === '') return false;
              return true;
            });

            const hasCollapsibleColumns = displayableColumns.length > 6;

            return (
              <MobileCard
                key={inx}
                onClick={e => {
                  onRowClick && !disableRowClick && onRowClick(item, inx);
                }}
                style={{
                  cursor: (onRowClick && !disableRowClick) ? 'pointer' : 'default',
                  ...(rowStyle ? rowStyle(item, inx) : {})
                }}
              >
                <MobileCardHeader>
                  <MobileCardTitleWrapper>
                    {chooseColumn && (
                      <MobileCardCheckbox onClick={e => e.stopPropagation()}>
                        {chooseColumn.render ? chooseColumn.render(null, item, inx) : null}
                      </MobileCardCheckbox>
                    )}
                    <MobileCardTitle>
                      {typeof headerText === 'string' ? capitializeFirstChar(headerText) : headerText}
                    </MobileCardTitle>
                  </MobileCardTitleWrapper>
                  <MobileCardActions>
                    {onRowClick && (
                      <span onClick={e => { e.stopPropagation(); onRowClick(item); }}>
                        {rowActionType === 'VIEW' ? <EyeIcon style={{ color: blue300 }} /> : <EditIcon style={{ color: cinnabar300 }} />}
                      </span>
                    )}
                  </MobileCardActions>
                </MobileCardHeader>
                <MobileCardBody>
                  {displayableColumns.map((column, cIdx) => {
                    const val = column.key && column.key.split('.').reduce((o, i) => o && o[i], item);
                    const isWorkflowStatus = column.name.toLowerCase() === "workflow status";
                    const displayValue = column.render ? column.render(val, item, inx) : (typeof val === 'string' ? capitializeFirstChar(val) : val);
                    const statusColors = isWorkflowStatus ? getWorkflowStatusColors(val) : null;

                    // If card is collapsed, only show the first 6 displayable data columns
                    if (!isExpanded && cIdx >= 6) {
                      return null;
                    }

                    return (
                      <MobileCardRow key={column.key || cIdx}>
                        <MobileCardLabel>{column.name && column.name.trim() ? column.name : "Actions"}</MobileCardLabel>
                        {isWorkflowStatus ? (
                          <StatusBadge bg={statusColors.bg} color={statusColors.color} border={statusColors.border}>
                            {displayValue}
                          </StatusBadge>
                        ) : (
                          <MobileCardValue>
                            {displayValue}
                          </MobileCardValue>
                        )}
                      </MobileCardRow>
                    );
                  })}
                </MobileCardBody>
                {hasCollapsibleColumns && (
                  <ExpandToggleButton
                    onClick={e => {
                      e.stopPropagation();
                      this.toggleCardExpand(inx);
                    }}
                  >
                    {isExpanded ? 'Show Less' : 'Show More'}
                    <ExpandMoreIcon
                      style={{
                        fontSize: 18,
                        transform: `rotate(${isExpanded ? 180 : 0}deg)`,
                        transition: 'transform 0.2s ease',
                      }}
                    />
                  </ExpandToggleButton>
                )}
              </MobileCard>
            );
          })}
        </MobileContainer>
      );
    }

    return columns.length > 0 ? (
      <StyledTable position={position} length={columns.length} columns={columns} rightPanel={rightPanel}>
        <THEAD hasFixedElements={hasFixedElements} fluidTable={fluidTable} isBorder={isBorder}>
          {columns.filter(Boolean).map((column, idx) => (
    <THD
      key={column.key || idx}
      width={column.width}
      fluidTable={fluidTable}
      noPadding={column.noPadding}
      sortable={column.sortable}
      whitespace={column.whitespace}
      onClick={column.sortable ? () => updateOrder(column.key, order[column.key] === "ASC" ? "DSC" : "ASC") : null}
      isBorder={isBorder}
      title={typeof column.name === 'string' ? column.name : ''}
    >
      {column.name || ""}
      {column?.sortable ? (
        order[column.key] === "ASC" ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />
      ) : null}
    </THD>
  ))}
        </THEAD>
        <TBODY>
          {data.map((item, inx) => (
          
            <TR
              key={inx}
              fluidTable={fluidTable}
              height={rowHeight}
              clickable={onRowClick}
              onClick={e => {
                onRowClick && !disableRowClick && onRowClick(item, inx);
              }}
              disablePointer={disableRowClick}
              isSelected={item?.related_workflow_object && selectedOutletRows.includes(item?.related_workflow_object?.id)}
              isCursor={isCursor}
              isPosition={isPosition}
              isBorder={isBorder}
              status={item?.related_workflow_object?.current_status}
              useStatusColor={useStatusColor}
              isHovered={hoveredRowIndex === inx}
              onMouseEnter={() => onRowHover && onRowHover(inx)}
              onMouseLeave={() => onRowHover && onRowHover(null)}
              style={rowStyle ? rowStyle(item, inx) : undefined}
            >
              {columns.map((column, idx) => {
                const value = column.key && column.key.split('.').reduce((o, i) => o && o[i], item);
                return (
                  <TD
                    key={column.key || idx}
                    isSelected={selectedOutletRows.includes(item?.related_workflow_object?.id)}
                    whitespace={column.whitespace || 'nowrap'}
                    lastRecord={inx === data.length - 1}
                    fluidTable={fluidTable}
                    onClick={e => {
                      column.stopPropagation && e.stopPropagation();
                      column.onClick && column.onClick(value, item, e);
                    }}
                    height={column.height}
                    width={column.width}
                    hoverable={typeof column.onClick === 'function'}
                    wordBreak={column.wordBreak}
                    noPadding={column.noPadding}
                    isBorder={isBorder}
                    style={rowStyle ? rowStyle(item, inx) : undefined}
                  >
                    {column.render ? (
                      column.render(value, item, inx)
                    ) : (
                      <div clickable={typeof column.onClick === 'function'}>
                        {typeof value === 'string' ? capitializeFirstChar(value) : value}
                      </div>
                    )}
                  </TD>
                );
              })}

              {(onRowClick || isCursor) && !disableRowClick && (
                <RowAction
                  rowActionElementWidth={rowActionElementWidth}
                  onClick={e => {
                    e.stopPropagation();
                    onRowClick && onRowClick(item);
                  }}
                >
                  {rowActionType === 'VIEW' ? '' : rowActionType === 'CUSTOM' ? rowActionElement : <EditIcon style={{ color: cinnabar300 }} />}
                </RowAction>
              )}
              {onRowClick && disableRowClick && (
                <RowAction rowActionElementWidth={rowActionElementWidth}>
                  {rowActionType === 'VIEW' ? (
                    <EyeIcon
                      style={{ color: blue300 }}
                      onClick={e => {
                        e.stopPropagation();
                        onRowClick && onRowClick(item);
                      }}
                    />
                  ) : rowActionType === 'CUSTOM' ? (
                    rowActionElement
                  ) : (
                    <EditIcon
                      style={{ color: cinnabar300 }}
                      onClick={e => {
                        e.stopPropagation();
                        onRowClick && onRowClick(item);
                      }}
                    />
                  )}
                </RowAction>
              )}
            </TR>
          ))}
        </TBODY>
      </StyledTable>
    ) : null;
  }
}

export default Table;

